import { GameMode } from './GameMode.js';
import { DM_RULES } from './rules.js';
import { MatchStats } from './MatchStats.js';
import { computeAwards } from './awards.js';

/**
 * Free-for-all: a fixed bot population, everyone hostile to everyone,
 * respawns forever — but the MATCH now ends.
 *
 * `ffa` is the load-bearing part. Bots are all spawned on team 'red' — the team
 * field is what bomb mode needs — and `Enemy.isFoe` compares teams, so without
 * this flag every bot was friendly to every other bot and the entire population
 * did nothing but converge on the player. Now they fight each other, and the
 * match carries on whether or not you are in the middle of it.
 *
 * ## Why a bot keeps its slot
 *
 * A dead bot is replaced by a NEW `Enemy` with a new id and a new callsign, so
 * without a stable identity a fifteen-minute match ends with a scoreboard of
 * forty strangers who each got one kill. The replacement inherits the dead
 * bot's slot, name and tally, which is also what makes the live scoreboard
 * stop reshuffling every six seconds.
 */
export class Deathmatch extends GameMode {
  static id = 'dm';

  /** Read by Game.spawnEnemy and stamped onto each bot. */
  static ffa = true;

  constructor(game, opts) {
    super(game, opts);
    this.clock = DM_RULES.matchTime;
    this.over = false;
    this.matchWinner = null;
    this.awards = null;
    /** Per-match stats for the award screens. Game feeds it through its hooks. */
    this.stats = new MatchStats();
  }

  start() {
    const g = this.game;
    for (let i = 0; i < DM_RULES.botCount; i++) {
      const e = g.spawnEnemy('red');
      if (e) e.slot = `dm${i}`;
    }
  }

  update(dt) {
    const g = this.game;
    // Ground covered, for the LONG RUNNER award. Movement has no single choke
    // point offline, so it is sampled rather than hooked.
    this.stats.sample([g.player, ...g.enemies]);

    if (this.over) {
      this.clock -= dt;
      if (this.clock <= -DM_RULES.intermission) this._newMatch();
      return;
    }

    this.clock -= dt;
    // Two ways to finish. The score limit is the real one; the clock is the
    // backstop for a lobby that is not scoring, and it has to exist or "the
    // match ends" is a promise the mode cannot keep.
    //
    // Read straight off the live rows rather than through `toRows()`: that
    // normalises and copies every row, and this runs sixty times a second for
    // a quarter of an hour.
    let best = null;
    for (const r of this.stats.rows.values()) {
      if (!best || r.kills > best.kills) best = r;
    }
    this.leader = best;
    if ((best?.kills ?? 0) >= DM_RULES.scoreLimit) return this._endMatch('score');
    if (this.clock <= 0) return this._endMatch('time');
  }

  _endMatch(reason) {
    this.over = true;
    this.reason = reason;
    this.clock = 0;
    const rows = this.stats.toRows();
    const top = rows.reduce((a, r) => (a && a.kills >= r.kills ? a : r), null);
    this.matchWinner = top?.id ?? null;
    this.awards = computeAwards(rows);
    // `ffa` here means "no sides": the result page is one ranked list rather
    // than two team panels, and the models wear their own colours rather than
    // a side's.
    this.game.matchAwards.show({
      ffa: true,
      winner: 'ffa',
      winnerName: top?.name ?? '—',
      winnerScore: top?.kills ?? 0,
      reason,
      mine: null,
      awards: this.awards,
      rows,
    });
  }

  /** Wipe the slate and play another one. */
  _newMatch() {
    const g = this.game;
    this.over = false;
    this.matchWinner = null;
    this.awards = null;
    this.reason = null;
    this.clock = DM_RULES.matchTime;
    this.stats.reset();
    for (const e of g.enemies) { e.kills = 0; e.deaths = 0; }
    g.hud.kills = 0;
    g.hud.score = 0;
    g.deaths = 0;
    g.matchAwards.hide();
  }

  onEnemyDeath(enemy) {
    // One-for-one replacement keeps the population steady, and the replacement
    // inherits who the dead bot WAS — see the class comment.
    const { slot, name, kills, deaths } = enemy;
    setTimeout(() => {
      if (this.game.destroyed || this.over) return;
      const e = this.game.spawnEnemy('red');
      if (!e) return;
      e.slot = slot;
      e.name = name;
      e.kills = kills;
      e.deaths = deaths;
    }, DM_RULES.respawnDelay * 1000);
  }

  onPlayerDeath() {
    this.game.respawnTimer = DM_RULES.playerRespawn;
    return false;          // let Game do its normal respawn
  }

  decorateHud(s) {
    // Deliberately minimal. Deathmatch's HUD is the one every regression probe
    // drives, so this adds the two values the match clock needs and nothing
    // that could turn a round-mode widget on.
    s.dmClock = Math.max(0, this.clock);
    s.dmLimit = DM_RULES.scoreLimit;
  }
}
